import {Component, inject, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {PageHeaderComponent} from '../../util/design/page-header/page-header.component';
import {EmptyStateComponent} from '../../util/design/empty-state/empty-state.component';
import {AssignedRuleset, CheckDefinition, MyChecksService} from '../my-checks.service';
import {SharedProject, WorkflowService} from '../../../services/workflow.service';
import {
  CatalogService,
  CatalogSubmission,
  catalogSourceKey,
  latestSubmissionBySource,
  rememberLatestSubmission,
} from '../../../services/catalog.service';
import {StateConfigService} from '../../../services/state/state-config.service';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicPopupComponent,
  BicTextareaComponent,
  BicToastService,
  BicToggleComponent,
  TooltipDirective
} from '@buildinginformationcloud/bic-ui-components';
import {
  faArrowDownWideShort,
  faArrowUpWideShort,
  faBuilding,
  faCube,
  faMagnifyingGlass,
  faPlus,
  faStar,
} from '@fortawesome/pro-solid-svg-icons';
import {CheckCardAction, CheckCardComponent} from './check-card/check-card.component';
import {CatalogSubmissionPromptComponent} from '../shared/catalog-submission-prompt/catalog-submission-prompt.component';
import {ShareWithProjectsPromptComponent} from '../shared/share-with-projects-prompt/share-with-projects-prompt.component';
import {serverErrorMessage} from '../shared/server-error-message';
import {DuplicateLinkedCheckPromptComponent} from '../shared/duplicate-linked-check-prompt/duplicate-linked-check-prompt.component';
import {ReferencedRuleset} from '../../../services/workflow.service';
import {
  ProjectOwnershipMode,
  ProjectOwnershipPromptComponent,
} from '../shared/project-ownership-prompt/project-ownership-prompt.component';
import {SkeletonCardComponent} from '../../util/skeleton-card/skeleton-card.component';
import {CheckPinService, PinScope, PinState} from '../../../services/check-pin.service';
import {PermissionService} from '../../../services/permission/permission.service';
import {BicFilterComponent, BicFilterConfig} from '../../util/design/bic-filter/bic-filter.component';
import {MyCheckTabsComponent} from '../shared/my-check-tabs/my-check-tabs.component';
import {FavoritesPromptComponent} from '../shared/favorites-prompt/favorites-prompt.component';
import {
  CreateCheckRunPromptComponent,
} from '../shared/create-check-run-prompt/create-check-run-prompt.component';
import {
  DuplicateCheckPromptComponent,
  DuplicateCheckSelection,
} from '../shared/duplicate-check-prompt/duplicate-check-prompt.component';
import {CheckRunScheduleView} from '../../../api/model/checkRunScheduleView';
import {CheckRunScheduleApi} from '../../../services/check-run-schedule/check-run-schedule.api';
import {
  CheckRunSchedulePopupComponent,
} from '../check-run-schedules/check-run-schedule-popup/check-run-schedule-popup.component';

/** In myChecks werden nur benutzerdefinierte Checks gezeigt. */
type OriginFilter = 'CUSTOM';

/** `own` = eigene Prüfläufe, `catalog` = die von der BIC bereitgestellten. */
export type MyChecksOverviewMode = 'own' | 'catalog';

type CheckSortField = 'name' | 'status' | 'updated';

interface SortFieldOption {
  field: CheckSortField;
  label: string;
  ascLabel: string;
  descLabel: string;
}

const CHECK_SORT_FIELD_OPTIONS: SortFieldOption[] = [
  {field: 'name', label: 'Name', ascLabel: 'A–Z', descLabel: 'Z–A'},
  {field: 'status', label: 'Status', ascLabel: 'Aktiv zuerst', descLabel: 'Entwurf zuerst'},
  {field: 'updated', label: 'Zuletzt geändert', ascLabel: 'Älteste zuerst', descLabel: 'Neueste zuerst'},
];

/** Reihenfolge für die Sortierung nach Status: fertige/aktive zuerst, Entwürfe danach. */
function checkStatusRank(status: CheckDefinition['status']): number {
  const order: Record<string, number> = {ACTIVE: 0, DRAFT: 1, INACTIVE: 2, ARCHIVED: 3};
  return order[status] ?? 4;
}

/** Robuster Zeitwert für die Sortierung – fehlendes/ungültiges Datum landet am Ende. */
function dateValue(value: string | Date | undefined): number {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

@Component({
  selector: 'my-checks-overview',
  standalone: true,
  imports: [
    PageHeaderComponent,
    EmptyStateComponent,
    FaIconComponent,
    BicPopupComponent,
    BicButtonComponent,
    BicDropdownComponent,
    BicDropdownTriggerDirective,
    BicInputComponent,
    BicTextareaComponent,
    BicToggleComponent,
    FormsModule,
    CheckCardComponent,
    SkeletonCardComponent,
    TooltipDirective,
    BicFilterComponent,
    CatalogSubmissionPromptComponent,
    ShareWithProjectsPromptComponent,
    ProjectOwnershipPromptComponent,
    DuplicateLinkedCheckPromptComponent,
    MyCheckTabsComponent,
    DuplicateCheckPromptComponent,
    FavoritesPromptComponent,
    CreateCheckRunPromptComponent,
    CheckRunSchedulePopupComponent,
  ],
  templateUrl: './my-checks-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./my-checks-overview.component.scss'],
})
export class MyChecksOverviewComponent implements OnInit {
  /** Gesetzt von der myCheck-Hülle anhand des aktiven Tabs. */
  @Input() mode: MyChecksOverviewMode = 'own';

  stateConfigService = inject(StateConfigService);
  /** Ladezustand */
  loading = true;
  /** Button-Loading-States */
  creating = false;
  saving = false;
  deleting = false;
  /** Alle in myChecks sichtbaren Checks (nur benutzerdefiniert). */
  checks: CheckDefinition[] = [];
  /**
   * myChecks, die uns von anderen Mandanten freigegeben wurden. Eigener Abschnitt statt Filter:
   * Freigaben sind zu den eigenen Checks additiv und müssen ohne Interaktion sichtbar sein.
   */
  sharedChecks: CheckDefinition[] = [];
  /** Aktiver Herkunfts-Filter */
  originFilter: OriginFilter = 'CUSTOM';
  /** Suchbegriff */
  searchQuery = '';
  /** Status-Filter (Aktiv/Archiviert) über der Übersicht. */
  filterConfig: BicFilterConfig[] = [
    {
      key: 'status',
      displayName: 'Status',
      type: 'text',
      options: ['Archiviert'],
    },
  ];
  activeFilters: { [key: string]: any[] } = {};
  /** Für das aktuelle Projekt sichtbare, angeheftete Check-Definitionen. */
  private pinnedDefinitionIds = new Set<string>();
  private pinnedScopeMap = new Map<string, PinScope>();
  showCreatePopup = false;
  /** Settings popup state */
  showSettingsPopup = false;
  settingsName = '';
  settingsDescription = '';
  settingsScheduledTrigger = false;
  /** Pin popup state */
  showPinPopup = false;
  pinTarget: CheckDefinition | null = null;
  pinLoading = false;
  pinSaving = false;
  pinProjectScoped = false;
  pinOrganisationScoped = false;
  pinAlreadyPinned = false;
  showFavoritesPopup = false;
  /** FontAwesome Icons */
  protected readonly faPlus = faPlus;
  protected readonly faStar = faStar;
  protected readonly faBuilding = faBuilding;
  protected readonly faCube = faCube;
  protected readonly faMagnifyingGlass = faMagnifyingGlass;
  protected readonly faArrowDownWideShort = faArrowDownWideShort;
  protected readonly faArrowUpWideShort = faArrowUpWideShort;
  private router = inject(Router);
  private myChecksService = inject(MyChecksService);
  private flowService = inject(WorkflowService);
  private toastService = inject(BicToastService);
  private pinService = inject(CheckPinService);
  private permissionService = inject(PermissionService);
  private catalogService = inject(CatalogService);
  private settingsCheck: CheckDefinition | null = null;
  private scheduleApi = inject(CheckRunScheduleApi);

  /**
   * Zeitplan je Prüflauf im aktuellen Projekt (eigene Prüfläufe und BIC Katalog). Einmal je Laden
   * gesetzt; die Karte bekommt per `.get` das bestehende Objekt.
   */
  schedulesByDefinitionId = new Map<string, CheckRunScheduleView>();
  /** Prüflauf, für den das Zeitplan-Popup offen ist (null = geschlossen). */
  scheduleCheck: CheckDefinition | null = null;
  /** Bestehender Zeitplan dieses Prüflaufs beim Öffnen des Popups. */
  scheduleCheckSchedule: CheckRunScheduleView | null = null;

  /** myCheck verwalten – erstellen, duplizieren, teilen, bearbeiten, anheften. */
  get canManageMyChecks(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  protected readonly noManageMyChecksTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um Prüfläufe zu verwalten.';

  get isCatalog(): boolean {
    return this.mode === 'catalog';
  }

  /** Gefilterte Checks anhand von Suchbegriff und Herkunfts-Filter */
  get filteredChecks(): CheckDefinition[] {
    return this.filterChecks(this.checks);
  }

  /** Katalog-Prüflauf, für den der Duplizieren-Dialog offen ist (null = geschlossen). */
  duplicateTarget: CheckDefinition | null = null;
  duplicateRulesets: AssignedRuleset[] = [];
  duplicateLoading = false;
  duplicateLoadFailed = false;
  duplicating = false;

  /** Prüflauf, für den der Katalog-Einreichungsdialog offen ist (null = geschlossen). */
  catalogSubmissionTarget: CheckDefinition | null = null;
  /** Darf aus dieser Stage in den Katalog eingereicht werden? (nur für Admins geladen) */
  catalogSubmissionAllowed = false;
  /** Letzte eigene Katalog-Einreichung je Prüflauf – Grundlage der Karten-Badges. */
  private catalogSubmissions = new Map<string, CatalogSubmission>();
  /** Höchste freigegebene Katalog-Version je catalogKey, aus den Freigaben (sharedChecks). */
  private catalogVersionsByKey = new Map<string, number>();

  /** myCheck, für den der Dialog „mit Projekten teilen" offen ist (null = geschlossen). */
  projectShareCheck: CheckDefinition | null = null;
  /** In dieses Projekt geteilter myCheck, dessen Entfernen gerade bestätigt wird. */
  unshareHereCheck: CheckDefinition | null = null;
  unsharingHere = false;
  /** Aus einem anderen Projekt geteilter Prüflauf, für den die Duplizieren-Rückfrage offen ist. */
  linkedDuplicateCheck: CheckDefinition | null = null;
  /** Seine hier nicht verfügbaren myChecks. */
  linkedDuplicateRulesets: ReferencedRuleset[] = [];
  linkedDuplicating = false;

  /** Geteilter Prüflauf, für den der Eigentümer-/Archiv-Dialog offen ist (null = geschlossen). */
  ownershipCheck: CheckDefinition | null = null;
  ownershipMode: ProjectOwnershipMode = 'delete';

  /** Nach Übergabe oder Entfernen neu laden – Eigentümer, Badges und Projektliste ändern sich. */
  onOwnershipDone(): void {
    this.ownershipCheck = null;
    this.loadChecks();
  }

  /** Projekt der Übersicht – die Karten unterscheiden damit Eigentümer- und Zielprojekt. */
  get currentProjectId(): number | undefined {
    return this.stateConfigService.selectedProject?.id;
  }

  /** Ob ein Check im aktuellen Projektkontext angeheftet ist. */
  isCheckPinned(check: CheckDefinition): boolean {
    return this.pinnedDefinitionIds.has(check.id);
  }

  /**
   * Ob organisationsweites Anheften möglich ist.
   * Temporär deaktiviert, da organisationsweites Anheften noch zu Problemen führt.
   */
  get canPinOrganisation(): boolean {
    return false;
  }

  /** Im Katalog gibt es den Filter nicht: eine Freigabe kann der Empfänger nicht archivieren. */
  get showArchived(): boolean {
    return !this.isCatalog && !!this.activeFilters['status']?.includes('Archiviert');
  }

  ngOnInit(): void {
    this.loadChecks();
    this.loadCatalogState();
  }

  /** Reagiert auf Änderungen des Status-Filters (Aktiv/Archiviert). */
  onFiltersChange(event: { [key: string]: any[] }): void {
    const wasArchived = this.showArchived;
    this.activeFilters = event;
    if (this.showArchived !== wasArchived) {
      this.loadChecks();
    }
  }

  /** Gefilterte Freigaben – dieselbe Suche greift in beiden Abschnitten. */
  get filteredSharedChecks(): CheckDefinition[] {
    return this.filterChecks(this.sharedChecks);
  }

  /** Check, der auf Löschbestätigung wartet (null = kein Lösch-Popup offen). */
  checkToDelete: CheckDefinition | null = null;

  /** "Neuer Prüflauf" – öffnet das Popup */
  createNewCheck(): void {
    if (!this.canManageMyChecks) return;
    this.showCreatePopup = true;
  }


  /** Popup: "Erstellen" – erstellt den Prüflauf und navigiert zum Builder */
  onConfirmCreate(name: string): void {
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) return;

    this.creating = true;
    const manifest = this.flowService.createEmptyManifest();
    manifest.name = name;
    manifest.description = '';

    this.flowService.saveDefinition(manifest, projectId).subscribe({
      next: def => {
        this.creating = false;
        this.showCreatePopup = false;
        // Kein Quickstart hier: ein Prüflauf wird immer im Expertenmodus konfiguriert. Der
        // Chat-geführte Einstieg gehört zu den myChecks (my-checks/:rulesetId/ki).
        void this.router.navigate([
          'projects',
          this.stateConfigService.selectedProject.id,
          'check-runs',
          'builder',
          def.id,
        ]);
      },
      error: () => {
        this.creating = false;
        this.toastService.error('Prüflauf konnte nicht erstellt werden');
      },
    });
  }

  /** Open settings popup for a check */
  openSettings(check: CheckDefinition): void {
    this.settingsCheck = check;
    this.settingsName = check.name;
    this.settingsDescription = check.description;
    this.settingsScheduledTrigger = false;
    this.showSettingsPopup = true;
  }

  /** Close settings popup */
  onSettingsClose(): void {
    this.showSettingsPopup = false;
    this.settingsCheck = null;
  }

  /** Save settings and update check */
  onSettingsSave(): void {
    if (!this.settingsCheck?.id) {
      this.showSettingsPopup = false;
      return;
    }

    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) {
      this.showSettingsPopup = false;
      return;
    }

    this.saving = true;
    this.flowService.getDefinition(this.settingsCheck.id).subscribe({
      next: def => {
        const manifest = this.flowService.parseManifest(def);
        manifest.name = this.settingsName.trim() || manifest.name;
        manifest.description = this.settingsDescription.trim();

        this.flowService.updateDefinition(this.settingsCheck!.id, manifest, projectId).subscribe({
          next: updated => {
            this.settingsCheck!.name = updated.name ?? this.settingsCheck!.name;
            this.settingsCheck!.description = this.settingsDescription.trim();
            this.saving = false;
            this.showSettingsPopup = false;
            this.settingsCheck = null;
            this.toastService.success('Einstellungen gespeichert');
          },
          error: () => {
            this.saving = false;
            this.toastService.error('Einstellungen konnten nicht gespeichert werden');
            this.showSettingsPopup = false;
          },
        });
      },
      error: () => {
        this.saving = false;
        this.toastService.error('Definition konnte nicht geladen werden');
        this.showSettingsPopup = false;
      },
    });
  }

  /** Einreichung ist durch – Dialog schließen und das Badge der Karte sofort aktualisieren. */
  onCatalogSubmitted(submissions: CatalogSubmission[]): void {
    this.catalogSubmissionTarget = null;
    const next = new Map(this.catalogSubmissions);
    for (const submission of submissions) {
      rememberLatestSubmission(next, submission);
    }
    this.catalogSubmissions = next;
  }

  closeCatalogSubmission(): void {
    this.catalogSubmissionTarget = null;
  }

  /** Letzte Katalog-Einreichung eines Prüflaufs (für das Karten-Badge). */
  catalogSubmissionFor(check: CheckDefinition): CatalogSubmission | null {
    return this.catalogSubmissions.get(catalogSourceKey('WORKFLOW', check.id)) ?? null;
  }

  /**
   * Duplikat einer Katalog-Kopie: die höhere Katalog-Version, die als Freigabe vorliegt, sonst
   * {@code null}. Verglichen wird über den gemeinsamen catalogKey.
   */
  newCatalogVersionFor(check: CheckDefinition): number | null {
    if (!check.sourceCatalogKey || check.sourceCatalogVersion == null) return null;
    const available = this.catalogVersionsByKey.get(check.sourceCatalogKey);
    return available != null && available > check.sourceCatalogVersion ? available : null;
  }

  /**
   * Katalog-Zustand für Admins: Einreichen erlaubt? + eigene Einreichungen (einmal, ohne Filter,
   * statt pro Karte). Für Nicht-Admins gibt es weder Menüeintrag noch Badge – die Endpunkte
   * verlangen GLOBAL_CHECKRULE_ADMIN, ein Aufruf wäre ein sicherer 403.
   */
  private loadCatalogState(): void {
    if (this.isCatalog || !this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) return;
    this.catalogService.getConfig().subscribe({
      next: config => (this.catalogSubmissionAllowed = config.submissionAllowed),
      error: err => this.toastService.error(
        serverErrorMessage(err, 'Katalog-Konfiguration konnte nicht geladen werden.')),
    });
    this.catalogService.listSubmissions().subscribe({
      next: submissions => (this.catalogSubmissions = latestSubmissionBySource(submissions)),
      error: err => this.toastService.error(
        serverErrorMessage(err, 'Katalog-Einreichungen konnten nicht geladen werden.')),
    });
  }

  /** Höchste Katalog-Version je catalogKey aus den Freigaben – für „Neue Version verfügbar". */
  private indexCatalogVersions(shares: CheckDefinition[]): void {
    const versions = new Map<string, number>();
    for (const share of shares) {
      if (!share.catalogKey || share.catalogVersion == null) continue;
      const current = versions.get(share.catalogKey);
      if (current == null || share.catalogVersion > current) versions.set(share.catalogKey, share.catalogVersion);
    }
    this.catalogVersionsByKey = versions;
  }

  /** Open workflow in the visual editor (read-only) */
  private viewInEditor(check: CheckDefinition): void {
    this.router.navigate(
      ['projects', this.stateConfigService.selectedProject.id, 'workflows', check.id],
      {queryParams: {readOnly: 'true'}},
    );
  }

  /** Speichert den gewählten Pin-Scope (Toggle-Semantik im Backend). */
  onPinSave(): void {
    if (!this.pinTarget?.id) {
      this.onPinClose();
      return;
    }
    this.pinSaving = true;
    this.pinService.setPin(this.pinTarget.id, {
      projectScoped: this.pinProjectScoped,
      organisationScoped: this.pinOrganisationScoped && this.canPinOrganisation,
    }).subscribe({
      next: () => {
        this.refreshPinnedChecks();
        this.pinSaving = false;
        this.showPinPopup = false;
        this.pinTarget = null;
        this.toastService.success('Prüflauf angeheftet');
      },
      error: () => {
        this.pinSaving = false;
        this.toastService.error('Anheften fehlgeschlagen');
      },
    });
  }

  /** Handle card actions from check-card component */
  onCardAction(event: { action: CheckCardAction; check: CheckDefinition }): void {
    const check = event.check;
    // Defensive: obwohl die UI die Aktionen bereits ausblendet, wird die Berechtigung
    // hier erneut geprüft, damit z.B. Direktaufrufe nicht durchrutschen.
    //
    // Bei zentral bereitgestellten Checks sind nur Ausführen und Duplizieren erlaubt. Öffnen,
    // Bearbeiten, Archivieren und Anheften fallen weg: die Definition gehört dem Adminbereich, nicht
    // diesem Projekt.
    if (check.shared && !['duplicate', 'execute'].includes(event.action)) return;

    switch (event.action) {
      case 'open':
      case 'edit':
        if (!this.canManageMyChecks) return;
        this.openCheck(check);
        break;
      case 'execute':
        if (!this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE')) return;
        this.executeCheck(check);
        break;
      case 'duplicate':
        if (!this.canManageMyChecks) return;
        this.duplicateCheck(check);
        break;
      case 'provide-globally':
        if (!this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) return;
        if (!this.catalogSubmissionAllowed) return;
        this.catalogSubmissionTarget = check;
        break;
      case 'share':
        if (!this.canManageMyChecks) return;
        this.projectShareCheck = check;
        break;
      case 'unshare-here':
        if (!this.canManageMyChecks) return;
        this.unshareHereCheck = check;
        break;
      case 'transfer-ownership':
        if (!this.canManageMyChecks) return;
        this.ownershipMode = 'transfer';
        this.ownershipCheck = check;
        break;
      case 'settings':
        if (!this.canManageMyChecks) return;
        this.openSettings(check);
        break;
      case 'archive':
        if (!this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_DELETE')) return;
        this.requestDeleteCheck(check);
        break;
      case 'restore':
        if (!this.canManageMyChecks) return;
        this.restoreCheck(check);
        break;
      case 'view-workflow':
        if (!this.canManageMyChecks) return;
        this.viewInEditor(check);
        break;
      case 'activate':
        if (!this.canManageMyChecks) return;
        this.activateCheck(check);
        break;
      case 'deactivate':
        if (!this.canManageMyChecks) return;
        this.deactivateCheck(check);
        break;
      case 'pin':
        if (!this.canManageMyChecks) return;
        this.pinCheck(check);
        break;
      case 'unpin':
        if (!this.canManageMyChecks) return;
        this.unpinCheck(check);
        break;
      case 'schedule':
        if (!this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE')) return;
        this.scheduleCheckSchedule = this.schedulesByDefinitionId.get(check.id) ?? null;
        this.scheduleCheck = check;
        break;
    }
  }

  /** Öffnet das Bestätigungs-Popup vor dem Löschen eines myChecks. */
  requestDeleteCheck(check: CheckDefinition): void {
    // Auch hier der Riegel: die Methode ist public und aus dem Template aufrufbar.
    if (check.shared) return;
    if (!this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_DELETE')) return;
    // Mit anderen Projekten geteilt (und hier Eigentümer): statt der einfachen Rückfrage die Wahl
    // zwischen Besitz übergeben und überall entfernen.
    const linkedIn = check.projectId != null && check.projectId !== this.currentProjectId;
    if ((check.sharedProjects?.length ?? 0) > 1 && !linkedIn) {
      this.ownershipMode = 'delete';
      this.ownershipCheck = check;
      return;
    }
    this.checkToDelete = check;
  }

  /** Übernimmt den neuen Freigabe-Stand in die Karte, ohne die ganze Liste neu zu laden. */
  onProjectSharesSaved(sharedProjects: SharedProject[]): void {
    const check = this.projectShareCheck;
    this.projectShareCheck = null;
    if (!check) return;
    // Nur noch der Eigentümer übrig: dann ist der myCheck nicht mehr geteilt.
    this.replaceCheck(check.id, {sharedProjects: sharedProjects.length > 1 ? sharedProjects : undefined});
  }

  /**
   * Entfernt einen aus einem anderen Projekt geteilten myCheck aus diesem Projekt. Der myCheck selbst
   * bleibt im Eigentümerprojekt bestehen, Berichte in diesem Projekt bleiben erhalten.
   */
  confirmUnshareHere(): void {
    const check = this.unshareHereCheck;
    const projectId = this.currentProjectId;
    if (!check || !projectId || this.unsharingHere) return;
    this.unsharingHere = true;
    this.flowService.unshareFromProject(check.id, projectId, projectId).subscribe({
      next: () => {
        this.checks = this.checks.filter(c => c.id !== check.id);
        this.unsharingHere = false;
        this.unshareHereCheck = null;
        this.toastService.success('Prüflauf aus diesem Projekt entfernt.');
      },
      error: err => {
        this.unsharingHere = false;
        this.toastService.error(serverErrorMessage(err, 'Entfernen fehlgeschlagen.'));
      },
    });
  }

  cancelUnshareHere(): void {
    if (this.unsharingHere) return;
    this.unshareHereCheck = null;
  }

  private replaceCheck(id: string, changes: Partial<CheckDefinition>): void {
    this.checks = this.checks.map(c => c.id === id ? {...c, ...changes} : c);
  }

  /** Entfernt alle vom User verwalteten Pins dieses Checks im aktuellen Projekt. */
  onPinRemove(): void {
    if (!this.pinTarget?.id) {
      this.onPinClose();
      return;
    }
    this.pinSaving = true;
    this.pinService.unpin(this.pinTarget.id).subscribe({
      next: () => {
        this.refreshPinnedChecks();
        this.pinSaving = false;
        this.showPinPopup = false;
        this.pinTarget = null;
        this.toastService.success('Prüflauf gelöst');
      },
      error: () => {
        this.pinSaving = false;
        this.toastService.error('Lösen fehlgeschlagen');
      },
    });
  }

  /** Navigate to flow editor for a check */
  private openCheck(check: CheckDefinition): void {
    this.router.navigate([
      'projects',
      this.stateConfigService.selectedProject.id,
      'check-runs',
      'builder',
      check.id,
    ]);
  }

  /** Lädt die Checks passend zum aktuellen Status-Filter (aktiv vs. archiviert) neu. */
  private loadChecks(): void {
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) return;
    this.loading = true;

    if (this.showArchived) {
      // Freigaben sind für Empfänger nie archivierbar – im Archiv gibt es sie also nicht.
      this.myChecksService.getArchivedChecks(projectId).subscribe({
        next: archived => {
          this.checks = [...archived];
          this.sharedChecks = [];
          this.indexCatalogVersions(this.sharedChecks);
          this.refreshPinnedChecks(projectId);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
      return;
    }

    this.loadSchedules(projectId);
    this.myChecksService.getAvailableChecks(projectId).subscribe({
      next: result => {
        this.checks = [...result.custom];
        this.sharedChecks = [...result.shared];
        this.indexCatalogVersions(this.sharedChecks);
        this.refreshPinnedChecks(projectId);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /**
   * Zeitpläne für die Badges der Karten. Ein Ladefehler blendet nur die Badges aus; die
   * Prüflauf-Liste hängt nicht davon ab.
   */
  private loadSchedules(projectId: number): void {
    this.scheduleApi.list(projectId).subscribe({
      next: schedules => {
        this.schedulesByDefinitionId = new Map(schedules.map(schedule => [schedule.definitionId, schedule]));
      },
      error: err => {
        console.error('Zeitpläne konnten nicht geladen werden', err);
        this.schedulesByDefinitionId = new Map();
      },
    });
  }

  closeSchedulePopup(): void {
    this.scheduleCheck = null;
    this.scheduleCheckSchedule = null;
  }

  /** Nach Speichern oder Löschen: Popup zu, Badges neu laden. */
  onScheduleChanged(): void {
    this.closeSchedulePopup();
    const projectId = this.currentProjectId;
    if (projectId) this.loadSchedules(projectId);
  }

  /** Bricht das Löschen ab und schließt das Popup. */
  cancelDeleteCheck(): void {
    if (this.deleting) return;
    this.checkToDelete = null;
  }

  /** Bestätigt das Löschen des vorgemerkten myChecks. */
  confirmDeleteCheck(): void {
    if (!this.checkToDelete || this.deleting) return;
    this.deleteCheck(this.checkToDelete);
  }

  /** Execute a check – navigates to my-checks/execute */
  private executeCheck(check: CheckDefinition): void {
    if (!check.id) return;
    if (check.slug) {
      this.router.navigate(
        ['projects', this.stateConfigService.selectedProject.id, 'check-runs', 'execute'],
        {queryParams: {workflow: check.slug}},
      );
      return;
    }
    this.router.navigate(
      ['projects', this.stateConfigService.selectedProject.id, 'check-runs', 'execute'],
      {queryParams: {definitionId: check.id}},
    );
  }

  /**
   * Dupliziert einen myCheck – serverseitig.
   *
   * <p>Vorher lief das clientseitig: Manifest laden, JSON parsen, umbenennen, neu speichern. Für
   * Freigaben kann das gar nicht funktionieren, weil der Empfänger das Manifest bewusst nicht
   * bekommt. Das Ergebnis läuft durch {@code toCheckDefinition} statt von Hand zusammengebaut zu
   * werden – die Handarbeit hat Felder der Vorlage mitgeschleppt (u.a. würde sie jetzt `shared`
   * mitkopieren).
   */
  private duplicateCheck(check: CheckDefinition): void {
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) return;

    // Katalog-Eintrag: erst fragen, ob die myChecks mitkopiert werden – ohne Kopien bleiben sie
    // schreibgeschützt.
    if (check.shared && check.shareId) {
      this.openDuplicateDialog(check, projectId);
      return;
    }

    // Aus einem anderen Projekt geteilt: erst klären, welche myChecks hier fehlen. Fehlt nichts,
    // wird direkt dupliziert; sonst fragt der Dialog, ob sie als eigene Kopien mitkommen.
    const linkedIn = check.projectId != null && check.projectId !== projectId;
    if (linkedIn) {
      this.flowService.listMissingRulesetsForDuplicate(check.id, projectId).subscribe({
        next: missing => {
          if (missing.length === 0) {
            this.runDuplicate(check, projectId, []);
            return;
          }
          this.linkedDuplicateRulesets = missing;
          this.linkedDuplicateCheck = check;
        },
        error: err => this.reportDuplicateError(err, 'Duplizieren fehlgeschlagen.'),
      });
      return;
    }

    this.runDuplicate(check, projectId, []);
  }

  onLinkedDuplicateConfirmed(copyRulesetIds: string[]): void {
    const check = this.linkedDuplicateCheck;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!check || !projectId || this.linkedDuplicating) return;
    this.linkedDuplicating = true;
    this.runDuplicate(check, projectId, copyRulesetIds, () => {
      this.linkedDuplicating = false;
      this.linkedDuplicateCheck = null;
    });
  }

  closeLinkedDuplicate(): void {
    if (this.linkedDuplicating) return;
    this.linkedDuplicateCheck = null;
  }

  private runDuplicate(check: CheckDefinition, projectId: number, copyRulesetIds: string[],
                       done?: () => void): void {
    this.myChecksService.duplicateCheck(check.id, projectId, copyRulesetIds).subscribe({
      next: created => {
        this.checks = [...this.checks, this.myChecksService.toCheckDefinition(created)];
        this.toastService.success(copyRulesetIds.length > 0
          ? 'Prüflauf samt myChecks dupliziert.'
          : 'Prüflauf dupliziert.');
        done?.();
      },
      error: err => {
        this.linkedDuplicating = false;
        this.reportDuplicateError(err, 'Duplizieren fehlgeschlagen.');
      },
    });
  }

  openFavoritesPopup(): void {
    this.showFavoritesPopup = true;
  }

  /** Übernimmt die im Dialog geänderten Pins in die Karten. */
  onFavoritesClosed(): void {
    this.showFavoritesPopup = false;
    this.refreshPinnedChecks();
  }

  private openDuplicateDialog(check: CheckDefinition, projectId: number): void {
    this.duplicateTarget = check;
    this.duplicateRulesets = [];
    this.duplicateLoadFailed = false;
    this.duplicateLoading = true;

    this.myChecksService.getAssignedRulesets(check.id, projectId).subscribe({
      next: assigned => {
        this.duplicateRulesets = assigned ?? [];
        this.duplicateLoadFailed = assigned === null;
        this.duplicateLoading = false;
      },
      error: () => {
        this.duplicateLoadFailed = true;
        this.duplicateLoading = false;
      },
    });
  }

  closeDuplicateDialog(): void {
    if (this.duplicating) return;
    this.duplicateTarget = null;
    this.duplicateRulesets = [];
    this.duplicateLoadFailed = false;
  }

  onDuplicateConfirmed(selection: DuplicateCheckSelection): void {
    const check = this.duplicateTarget;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!check?.shareId || !projectId || this.duplicating) return;

    const rulesets = selection.includeRules
      ? this.duplicateRulesets
        .filter(entry => selection.rulesetIds.includes(entry.ruleset.id))
        .map(entry => entry.ruleset)
      : [];

    this.duplicating = true;
    this.myChecksService.duplicateFromShareWithRules(check.shareId, projectId, rulesets).subscribe({
      next: result => {
        this.duplicating = false;
        this.duplicateTarget = null;
        this.duplicateRulesets = [];
        if (result.definition) {
          this.checks = [...this.checks, this.myChecksService.toCheckDefinition(result.definition)];
        }
        if (rulesets.length > 0 && !result.rulesRelinked) {
          // Als Erfolg zu melden wäre irreführend: der Nutzer träfe beim Bearbeiten wieder auf
          // den Schreibschutz.
          this.toastService.warning(
            'Prüflauf dupliziert, die Prüfregeln konnten aber nicht verknüpft werden. Die Kopien'
            + ' liegen unter „Eigene", die Zuordnung muss im Prüflauf angepasst werden.');
          return;
        }
        this.toastService.success(rulesets.length > 0
          ? `Prüflauf und ${rulesets.length} myCheck${rulesets.length === 1 ? '' : 's'} dupliziert.`
          : 'Prüflauf dupliziert.');
      },
      error: err => {
        this.duplicating = false;
        this.reportDuplicateError(err, 'Duplizieren fehlgeschlagen.');
      },
    });
  }

  /**
   * Fehlermeldung für Duplizieren/Share-Übernahme.
   *
   * <p>402 wird gesondert benannt: das ist das erschöpfte myCheck-Vorlagenkontingent, und die
   * Meldung (aus dem Backend oder aus der Vorabprüfung) nennt Limit und Bedarf. Ein generisches
   * "Duplizieren fehlgeschlagen" ließe den Nutzer ratlos zurück.
   */
  private duplicateErrorMessage(err: any, fallback: string): string {
    if (err?.status === 402) {
      return err?.error?.message ?? err?.message ?? fallback;
    }
    return fallback;
  }

  private reportDuplicateError(err: any, fallback: string): void {
    const message = this.duplicateErrorMessage(err, fallback);
    if (err?.status === 402) {
      this.toastService.warning(message);
      return;
    }
    this.toastService.error(message);
  }

  /** Archiviert einen Check (löscht nicht hart, siehe MyChecksService#archiveCheck). */
  private deleteCheck(check: CheckDefinition): void {
    if (!check.id) return;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) {
      this.toastService.error('Archivierung fehlgeschlagen: Projekt konnte nicht ermittelt werden');
      return;
    }

    this.deleting = true;
    this.myChecksService.archiveCheck(check.id, projectId).subscribe({
      next: () => {
        this.checks = this.checks.filter(c => c.id !== check.id);
        this.checkToDelete = null;
        this.deleting = false;
        this.toastService.success('Prüflauf archiviert');
      },
      error: err => {
        this.deleting = false;
        this.toastService.error(serverErrorMessage(err, 'Archivierung fehlgeschlagen'));
      },
    });
  }

  /** Stellt einen archivierten Check wieder her (Status → INACTIVE). */
  private restoreCheck(check: CheckDefinition): void {
    if (!check.id) return;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) return;

    (check as any)._activating = true;
    this.myChecksService.restoreCheck(check.id, projectId).subscribe({
      next: () => {
        this.checks = this.checks.filter(c => c.id !== check.id);
        this.toastService.success('Prüflauf wiederhergestellt');
      },
      error: () => {
        (check as any)._activating = false;
        this.toastService.error('Wiederherstellung fehlgeschlagen');
      },
    });
  }

  /** Activate a check (DRAFT/INACTIVE -> ACTIVE) */
  private activateCheck(check: CheckDefinition): void {
    if (!check.id) return;
    const projectId = this.stateConfigService.selectedProject?.id;
    (check as any)._activating = true;
    this.flowService.activateDefinition(check.id, projectId).subscribe({
      next: def => {
        check.status = (def.status as CheckDefinition['status']) ?? 'ACTIVE';
        (check as any)._activating = false;
        this.toastService.success('Prüflauf aktiviert');
      },
      error: () => {
        (check as any)._activating = false;
        this.toastService.error('Aktivierung fehlgeschlagen');
      },
    });
  }

  /** Deactivate a check (ACTIVE -> INACTIVE) */
  private deactivateCheck(check: CheckDefinition): void {
    if (!check.id) return;
    const projectId = this.stateConfigService.selectedProject?.id;
    (check as any)._activating = true;
    this.flowService.deactivateDefinition(check.id, projectId).subscribe({
      next: def => {
        check.status = (def.status as CheckDefinition['status']) ?? 'INACTIVE';
        (check as any)._activating = false;
        this.toastService.success('Prüflauf deaktiviert');
      },
      error: () => {
        (check as any)._activating = false;
        this.toastService.error('Deaktivierung fehlgeschlagen');
      },
    });
  }

  /** Schließt das Anheften-Popup. */
  onPinClose(): void {
    this.showPinPopup = false;
    this.pinTarget = null;
  }

  /** Pin a check – öffnet das Anheften-Popup und lädt den aktuellen Pin-Zustand. */
  private pinCheck(check: CheckDefinition): void {
    if (!check.id) return;
    this.pinTarget = check;
    this.pinProjectScoped = false;
    this.pinOrganisationScoped = false;
    this.pinAlreadyPinned = false;
    this.pinLoading = true;
    this.showPinPopup = true;

    this.pinService.getPinState(check.id).subscribe({
      next: (state: PinState) => {
        this.pinProjectScoped = state.projectScoped;
        this.pinOrganisationScoped = state.organisationScoped;
        this.pinAlreadyPinned = state.pinned || state.projectScoped || state.organisationScoped;
        this.pinLoading = false;
      },
      error: () => {
        this.pinLoading = false;
      },
    });
  }

  /** Entfernt die Anheftung direkt aus dem Kartenmenü. */
  private unpinCheck(check: CheckDefinition): void {
    if (!check.id) return;
    this.pinService.unpin(check.id).subscribe({
      next: () => {
        this.refreshPinnedChecks();
        this.toastService.success('Prüflauf gelöst');
      },
      error: () => {
        this.toastService.error('Lösen fehlgeschlagen');
      },
    });
  }

  /** Aktive Sortierung – wirkt auf eigene und geteilte Checks gleich. */
  sortField: CheckSortField = 'updated';
  sortDirection: 'asc' | 'desc' = 'desc';
  protected readonly sortFieldOptions = CHECK_SORT_FIELD_OPTIONS;

  /** Setzt Sortierfeld und -richtung direkt (je ein Pfeil-Button pro Feld im Dropdown). */
  applySort(field: CheckSortField, direction: 'asc' | 'desc'): void {
    this.sortField = field;
    this.sortDirection = direction;
  }

  /** Filtert Checks anhand des Suchbegriffs und des Herkunfts-Filters */
  private filterChecks(checks: CheckDefinition[]): CheckDefinition[] {
    let result = checks;
    result = result.filter(c => c.origin === this.originFilter);
    const q = this.searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(c => c.name.toLowerCase().includes(q));
    }
    return this.sortChecks(result);
  }

  private sortChecks(checks: CheckDefinition[]): CheckDefinition[] {
    const sorted = [...checks];
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    switch (this.sortField) {
      case 'status':
        sorted.sort((a, b) => (checkStatusRank(a.status) - checkStatusRank(b.status)) * dir
          || a.name.localeCompare(b.name, 'de'));
        break;
      case 'updated':
        sorted.sort((a, b) => (dateValue(a.updatedAt ?? a.createdAt) - dateValue(b.updatedAt ?? b.createdAt)) * dir);
        break;
      default:
        sorted.sort((a, b) => a.name.localeCompare(b.name, 'de') * dir);
    }
    return sorted;
  }

  /** Lädt die im aktuellen Projekt sichtbaren Pins für die Menü-/Label-Darstellung. */
  private refreshPinnedChecks(projectId?: number): void {
    const pid = projectId ?? this.stateConfigService.selectedProject?.id;
    if (!pid) {
      this.pinnedDefinitionIds = new Set<string>();
      return;
    }

    this.pinService.listPins(pid).subscribe({
      next: pins => {
        const visiblePins = pins.filter(pin => pin.scope === 'ORGANISATION' || pin.projectId === pid);
        this.pinnedDefinitionIds = new Set<string>(visiblePins.map(pin => pin.definitionId));
        this.pinnedScopeMap = new Map(visiblePins.map(pin => [pin.definitionId, pin.scope]));
      },
      error: () => {
        this.pinnedDefinitionIds = new Set<string>();
        this.pinnedScopeMap = new Map();
      },
    });
  }

}
